import { CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  numberAttribute,
} from '@angular/core';
import { ValidationErrors, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { filter, of, switchMap, take, tap } from 'rxjs';
import {
  GotoQuestionAction,
  Interaction,
  InteractionQuestion,
} from '../../../../models/interactions.model';
import { QuestionTreeNode } from '../../../../views/interaction-editor/interaction-editor.component';
import { ConfirmHandler } from '../../../dialog/dialog.model';
import { DialogService } from '../../../dialog/dialog.service';
import { InteractionEditorStateService } from '../../services/interaction-editor-state.service';
import { QuestionListItemComponent } from '../question-list-item/question-list-item.component';

const QUESTIONS_DEFAULT_MAX_LENGTH = 10;
@Component({
  selector: 'app-question-list',
  imports: [CdkDropList, MatIcon, MatIconButton, QuestionListItemComponent],
  templateUrl: './question-list.component.html',
  styleUrl: './question-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionListComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _interactionEditorState = inject(InteractionEditorStateService);

  protected readonly selectedQuestionId =
    this._interactionEditorState.selectedQuestionId;

  protected readonly selectedAnswerId = this._interactionEditorState.selectedAnswerId;

  protected readonly totalQuestions = computed(() => this.nodes().length);

  protected readonly canAddQuestion = computed(
    () => this.totalQuestions() < this.maxLength()
  );

  public readonly nodes = input<QuestionTreeNode[]>([]);

  public readonly maxLength = input(QUESTIONS_DEFAULT_MAX_LENGTH, {
    transform: numberAttribute,
  });

  public readonly disableControls = input(false, { transform: booleanAttribute });

  handleQuestionAdd() {
    if (this.disableControls()) {
      console.warn('Unable to add question. Editor is locked');
      return;
    }

    if (!this.canAddQuestion()) {
      console.warn('Unable to add question. Maximum of questions reached');
      return;
    }

    this._handlePromptDialog((result) =>
      of(result).pipe(
        filter((result) => result != null),
        tap((result) => {
          this._interactionEditorState.emitEvent({
            entity: 'questions',
            action: 'create',
            value: result,
          });
        }),
        switchMap(() => this._interactionEditorState.status$),
        filter((state) => state === 'idle'),
        take(1)
      )
    );
  }

  handleQuestionEdit() {
    if (this.disableControls()) {
      console.warn('Unable to edit question. Editor is locked');
      return;
    }

    const selectedQuestionId = this.selectedQuestionId();

    if (!selectedQuestionId) {
      console.warn('No question selected.');

      return;
    }

    const question = this.nodes().find((node) => node._id === selectedQuestionId);

    if (!question) {
      console.warn('No question found with given id.');
      return;
    }

    this._handlePromptDialog(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          tap((result) => {
            this._interactionEditorState.emitEvent({
              entity: 'questions',
              action: 'update',
              id: selectedQuestionId,
              value: result,
            });
          }),
          switchMap(() => this._interactionEditorState.status$),
          filter((state) => state === 'idle'),
          take(1)
        ),
      {
        title: 'Spraakbericht bewerken',
        value: question.value,
      }
    );
  }

  handleQuestionDelete() {
    if (this.disableControls()) {
      console.warn('Unable to delete question. Editor is locked');
      return;
    }

    const selectedQuestionId = this.selectedQuestionId();
    if (!selectedQuestionId) {
      console.warn('No question selected');
      return;
    }

    this._dialog.confirm({
      title: 'Spraakbericht verwijderen?',
      message:
        'Weet je zeker dat je dit spraakbericht wilt verwijderen? Ook de bijbehorende antwoorden en acties worden dan verwijderd',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () => {
        this._interactionEditorState.emitEvent({
          entity: 'questions',
          action: 'delete',
          id: selectedQuestionId,
        });

        return this._interactionEditorState.status$.pipe(
          filter((state) => state === 'idle'),
          take(1)
        );
      },
    });
  }

  handleQuestionDropped(
    event: CdkDragDrop<QuestionTreeNode[], unknown, QuestionTreeNode>
  ) {
    if (this.disableControls()) {
      console.warn('Unable to sort question. Editor is locked');
      return;
    }

    const { currentIndex, previousIndex } = event;

    if (currentIndex === previousIndex) {
      return;
    }

    const previousState = this._interactionEditorState.interaction();

    if (!previousState) {
      console.warn('No interaction state found.');

      return;
    }

    const updatedQuestions = this.reorderQuestions(
      previousState.questions,
      previousIndex,
      currentIndex
    );

    if (this._createsBackwardGotoQuestion(previousState, updatedQuestions)) {
      this._dialog.alert({
        title: 'Sorteren niet mogelijk',
        message:
          "Je kunt dit spraakbericht hier niet plaatsen: in deze volgorde zou een 'Ga naar vraag'-actie naar een eerder spraakbericht verwijzen. Let op dat dit ook een andere actie kan zijn dan degene die je juist wilde herstellen: verplaatsen verschuift alle 'Ga naar vraag'-acties tegelijk. Pas de betreffende actie eerst aan of verwijder hem, en sorteer daarna opnieuw.",
        confirmText: 'OK',
      });

      return;
    }

    this._interactionEditorState.setState({
      ...previousState,
      questions: updatedQuestions,
    });

    this._interactionEditorState.emitEvent({
      entity: 'questions',
      action: 'sort',
      id: event.item.data._id,
      index: currentIndex,
    });
  }

  /**
   * Whether reordering into `questions` would leave a 'Ga naar vraag' action
   * pointing at its own spraakbericht or at one above it.
   *
   * A reorder moves every jump in the interaction at once, so one that was
   * legal when it was added can end up running back up the list -- which the
   * backend refuses. Only jumps that are still forward in the current order are
   * judged, so an interaction that already holds a backward jump (made before
   * the rule existed) stays sortable.
   */
  private _createsBackwardGotoQuestion(
    interaction: Interaction,
    questions: InteractionQuestion[]
  ): boolean {
    const before = this._backwardGotoQuestionIds(interaction, interaction.questions);
    const after = this._backwardGotoQuestionIds(interaction, questions);

    return after.some((id) => !before.includes(id));
  }

  /** Ids of the 'Ga naar vraag' actions that jump backwards in `questions`. */
  private _backwardGotoQuestionIds(
    interaction: Interaction,
    questions: InteractionQuestion[]
  ): string[] {
    const questionIndexes = new Map(
      questions.map((question) => [question._id, question.index])
    );

    // The jump is measured from the spraakbericht its button belongs to, which
    // is the answer's, not whatever the action itself carries.
    const answerQuestions = new Map(
      interaction.answers.map((answer) => [answer._id, answer.questionId])
    );

    return interaction.actions
      .filter((action): action is GotoQuestionAction => action.type === 'goto-question')
      .filter((action) => {
        const ownerId = answerQuestions.get(action.answerId) ?? action.questionId;
        const ownerIndex = questionIndexes.get(ownerId);
        const targetIndex = questionIndexes.get(action.value);

        return ownerIndex != null && targetIndex != null && targetIndex <= ownerIndex;
      })
      .map((action) => action._id);
  }

  reorderQuestions(items: InteractionQuestion[], fromIndex: number, toIndex: number) {
    const copy = items.slice();

    moveItemInArray(copy, fromIndex, toIndex);

    const updatedQuestions = copy.map((question, index) => ({
      ...question,
      index,
    }));

    return updatedQuestions;
  }

  selectQuestion(question: QuestionTreeNode) {
    if (this.selectedQuestionId() !== question._id) {
      this.selectedAnswerId.set(null);
    }
    this.selectedQuestionId.set(question._id);
  }

  private _handlePromptDialog(
    callback: ConfirmHandler<string>,
    options?: { title?: string; value?: string }
  ) {
    this._dialog.prompt(
      {
        title: options?.title ?? 'Spraakbericht toevoegen',
        message: '',
        confirmText: 'Opslaan',
        cancelText: 'Annuleren',
        onConfirm: callback,
        input: {
          mode: 'input',
          label: 'Spraakbericht',
          placeholder: 'Vul een spraakbericht in',
          defaultValue: options?.value,
          validators: {
            required: {
              validator: Validators.required,
              errorMessage: 'Vul een spraakbericht in',
            },
            maxlength: {
              validator: Validators.maxLength(255),
              errorMessage: (args) => {
                const errors = args as ValidationErrors;

                return `Maximaal ${errors['requiredLength']} karakters`;
              },
            },
          },
        },
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }
}
